"use client";

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { ArrowRight, Home, Search, HelpCircle, TrendingUp, Settings, Zap, BookOpen } from 'lucide-react';
import Footer from '@/components/Footer';

export default function NotFound() {
  const router = useRouter();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for user data in cookies directly
    const checkUserStatus = () => {
      try {
        const userCookie = document.cookie
          .split('; ')
          .find(row => row.startsWith('user='))
          ?.split('=')[1];
        
        if (userCookie) {
          const userData = JSON.parse(decodeURIComponent(userCookie));
          setUser(userData);
        }
      } catch (_error) {
        console.log('No user cookie found');
      } finally {
        setLoading(false);
      }
    };

    checkUserStatus();
  }, []);

  const handleGoBack = () => {
    router.back();
  };

  const getPrimaryAction = () => {
    if (loading) {
      return {
        text: 'View Home',
        href: '/'
      };
    }
    if (user) {
      return {
        text: 'Return to Dashboard',
        href: '/dashboard'
      };
    } else {
      return {
        text: 'View Home',
        href: '/'
      };
    }
  };

  const primaryAction = getPrimaryAction();

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-grow">
        {/* Hero Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
          <div className="text-center">
            <div className="text-[8rem] font-bold text-gray-900 mb-8 leading-none">
              404
            </div>
            
            <div className="max-w-3xl mx-auto">
              <div className="bg-white p-8 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)]">
                <h1 className="text-3xl md:text-4xl font-bold mb-6 leading-tight">
                  This didn't make it into our portfolio
                </h1>
                
                <div className="text-lg text-gray-600 font-mono text-right mb-8">
                  This URL retired early.
                </div>
                
                <div className="space-y-4 text-gray-700 mb-8">
                  <p className="text-lg leading-relaxed">
                    The page you're looking for appears to have been moved, deleted, or never existed. 
                    This sometimes happens when financial data gets reorganised or URLs are updated.
                  </p>
                  
                  <p className="text-lg leading-relaxed">
                    Think of it like an investment that didn't quite work out - we learn from it and move on to better opportunities.
                  </p>
                </div>

                {/* Primary Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-4 mb-8 justify-center">
                  <Link
                    href={primaryAction.href}
                    className="bg-positive-600 hover:bg-positive-700 text-white font-bold py-3 px-8 rounded-md border-2 border-ink shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all hover:shadow-none text-center flex items-center justify-center"
                  >
                    {primaryAction.text}
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  
                  <button
                    onClick={handleGoBack}
                    className="bg-white hover:bg-gray-100 text-gray-800 font-bold py-3 px-8 rounded-md border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] transition-all hover:shadow-none"
                  >
                    Go Back
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Action Guidance Section */}
        <section className="py-16 bg-white border-t-2 border-b-2 border-black">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Next steps to get back on track</h2>
            
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <div className="p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-positive-50">
                <div className="w-12 h-12 flex items-center justify-center bg-positive-100 rounded-full mb-4">
                  <Search className="w-6 h-6 text-positive-600" />
                </div>
                <h3 className="text-xl font-bold mb-2">1. Check the URL</h3>
                <p className="text-gray-700">
                  Look for typos or incorrect paths in your browser's address bar
                </p>
              </div>
              
              <div className="p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-authority-50">
                <div className="w-12 h-12 flex items-center justify-center bg-authority-100 rounded-full mb-4">
                  <Home className="w-6 h-6 text-authority-600" />
                </div>
                <h3 className="text-xl font-bold mb-2">2. Use Navigation</h3>
                <p className="text-gray-700">
                  Access the main menu to find what you're looking for
                </p>
              </div>
              
              <div className="p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-danger-50">
                <div className="w-12 h-12 flex items-center justify-center bg-danger-100 rounded-full mb-4">
                  <HelpCircle className="w-6 h-6 text-danger-600" />
                </div>
                <h3 className="text-xl font-bold mb-2">3. Return Home</h3>
                <p className="text-gray-700">
                  Go back to your dashboard to access your portfolio
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Helpful Links Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-center mb-12">Looking for something specific?</h2>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {!loading && user ? (
                <>
                  <Link href="/dashboard" className="group block p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white hover:shadow-none transition-all">
                    <div className="w-12 h-12 flex items-center justify-center bg-positive-100 rounded-full mb-4 group-hover:bg-positive-200 transition-colors">
                      <Home className="w-6 h-6 text-positive-600" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 group-hover:text-positive-600 transition-colors">Dashboard</h3>
                    <p className="text-gray-600 text-sm">View your portfolio overview</p>
                  </Link>
                  
                  <Link href="/transactions" className="group block p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white hover:shadow-none transition-all">
                    <div className="w-12 h-12 flex items-center justify-center bg-authority-100 rounded-full mb-4 group-hover:bg-authority-200 transition-colors">
                      <Search className="w-6 h-6 text-authority-600" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 group-hover:text-authority-600 transition-colors">Transactions</h3>
                    <p className="text-gray-600 text-sm">Manage your transactions</p>
                  </Link>
                  
                  <Link href="/assets" className="group block p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white hover:shadow-none transition-all">
                    <div className="w-12 h-12 flex items-center justify-center bg-positive-100 rounded-full mb-4 group-hover:bg-positive-200 transition-colors">
                      <TrendingUp className="w-6 h-6 text-positive-600" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 group-hover:text-positive-600 transition-colors">Assets</h3>
                    <p className="text-gray-600 text-sm">Track your assets</p>
                  </Link>
                  
                  <Link href="/settings" className="group block p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white hover:shadow-none transition-all">
                    <div className="w-12 h-12 flex items-center justify-center bg-accessible-assets-100 rounded-full mb-4 group-hover:bg-accessible-assets-200 transition-colors">
                      <Settings className="w-6 h-6 text-accessible-assets-600" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 group-hover:text-accessible-assets-600 transition-colors">Settings</h3>
                    <p className="text-gray-600 text-sm">Manage your account</p>
                  </Link>
                </>
              ) : (
                <>
                  <Link href="/pricing" className="group block p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white hover:shadow-none transition-all">
                    <div className="w-12 h-12 flex items-center justify-center bg-positive-100 rounded-full mb-4 group-hover:bg-positive-200 transition-colors">
                      <TrendingUp className="w-6 h-6 text-positive-600" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 group-hover:text-positive-600 transition-colors">Pricing</h3>
                    <p className="text-gray-600 text-sm">See our plans</p>
                  </Link>
                  
                  <Link href="/features" className="group block p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white hover:shadow-none transition-all">
                    <div className="w-12 h-12 flex items-center justify-center bg-authority-100 rounded-full mb-4 group-hover:bg-authority-200 transition-colors">
                      <Zap className="w-6 h-6 text-authority-600" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 group-hover:text-authority-600 transition-colors">Solutions</h3>
                    <p className="text-gray-600 text-sm">Explore what we offer</p>
                  </Link>
                  
                  <Link href="/guides" className="group block p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white hover:shadow-none transition-all">
                    <div className="w-12 h-12 flex items-center justify-center bg-positive-100 rounded-full mb-4 group-hover:bg-positive-200 transition-colors">
                      <BookOpen className="w-6 h-6 text-positive-600" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 group-hover:text-positive-600 transition-colors">Guides</h3>
                    <p className="text-gray-600 text-sm">Learn about finance</p>
                  </Link>
                  
                  <Link href="/help" className="group block p-6 border-2 border-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] bg-white hover:shadow-none transition-all">
                    <div className="w-12 h-12 flex items-center justify-center bg-accessible-assets-100 rounded-full mb-4 group-hover:bg-accessible-assets-200 transition-colors">
                      <HelpCircle className="w-6 h-6 text-accessible-assets-600" />
                    </div>
                    <h3 className="text-lg font-bold mb-2 group-hover:text-accessible-assets-600 transition-colors">Help</h3>
                    <p className="text-gray-600 text-sm">Get support</p>
                  </Link>
                </>
              )}
            </div>
          </div>
        </section>
      </div>
      <Footer />
    </div>
  );
}